'use client';

import { useEffect } from 'react';
import { Link } from '@/components/LocaleLink';
import Image from 'next/image';
import { X, ShoppingCart, ArrowRight, Check } from 'lucide-react';
import { useCartStore } from '@/stores/cart';
import { useMiniCartUI } from '@/stores/mini-cart-ui';
import { formatPrice } from '@/lib/price';
import { useFreeShippingAmount } from '@/hooks/useFreeShippingThreshold';
import { useFraktoffert } from '@/hooks/useFraktoffert';

const PLACEHOLDER = '/placeholder-product.svg';

/**
 * Slide-out cart confirmation panel. Appears top-right after add-to-cart,
 * auto-closes after 7s, dismissible with Escape or backdrop click.
 *
 * Shows up to the last 3 items + cart total. Above the free-shipping threshold highlights free
 * shipping, otherwise nudges toward the threshold.
 */
export function MiniCartSlideout() {
  const isOpen = useMiniCartUI((s) => s.isOpen);
  const close = useMiniCartUI((s) => s.close);
  const items = useCartStore((s) => s.items);
  // Tröskeln kommer från store_settings.shipping_tiers, inte från en konstant här.
  // Hårdkodad 5000 innebar att progressbaren visade fel så fort fraktvillkoren ändrades.
  const FREE_SHIPPING_THRESHOLD = useFreeShippingAmount();
  const getTotal = useCartStore((s) => s.getTotal);
  const getItemCount = useCartStore((s) => s.getItemCount);
  // Vad frakten faktiskt kostar för just de här varorna — fri frakt gäller inte
  // långgods och pall, och korgen får inte lova något kassan tar betalt för.
  const fraktoffert = useFraktoffert(items.map((i) => ({ id: i.product.id, antal: i.quantity })), getTotal());

  // Escape closes
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  if (items.length === 0 && isOpen) {
    // Edge case: opened by stale event after clearing — just close.
    close();
    return null;
  }

  const total = getTotal();
  const count = getItemCount();
  const remainingToFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - total);

  const recent = items.slice(-3).reverse();

  return (
    <>
      {/* Backdrop — only on mobile so desktop layout isn't disturbed */}
      <div
        className={`fixed inset-0 bg-black/30 z-40 sm:hidden transition-opacity duration-200 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={close}
        aria-hidden="true"
      />

      {/* Slideout panel */}
      <div
        role="dialog"
        aria-labelledby="mini-cart-title"
        // `inert` (not aria-hidden) when closed: it hides from assistive tech AND
        // removes the off-screen panel's buttons/links from the tab order, fixing
        // the "aria-hidden element contains focusable descendants" a11y violation.
        inert={!isOpen}
        className={`fixed top-0 right-0 z-50 h-auto max-h-[100dvh] w-full sm:max-w-md sm:top-4 sm:right-4 sm:rounded-2xl bg-white shadow-2xl border-l sm:border border-gray-200 overflow-hidden transition-transform duration-300 ease-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-[110%]'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b bg-green-50">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-full bg-green-600 flex items-center justify-center shrink-0">
              <Check className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 id="mini-cart-title" className="font-semibold text-sm sm:text-base text-gray-900">
                Tillagd i varukorgen
              </h2>
              <p className="text-xs text-gray-600">{count} {count === 1 ? 'produkt' : 'produkter'} totalt</p>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Stäng"
            className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-white/60 text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Recent items list */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-3 sm:py-4 space-y-3 min-h-0">
          {recent.map((item) => {
            const img = item.product.images?.find((i) => i.cover)?.external_url
              || item.product.images?.[0]?.external_url
              || PLACEHOLDER;
            return (
              <div key={item.product.id} className="flex items-start gap-3">
                <div className="relative shrink-0 h-14 w-14 sm:h-16 sm:w-16 rounded-lg bg-gray-50 border border-gray-100 overflow-hidden">
                  <Image
                    src={img}
                    alt={item.product.name}
                    fill
                    sizes="64px"
                    className="object-contain p-1"
                    referrerPolicy="no-referrer"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 line-clamp-2 leading-tight">
                    {item.product.name}
                  </p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-500">{item.quantity} st</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatPrice(item.product.price * item.quantity)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          {items.length > recent.length && (
            <p className="text-xs text-gray-500 text-center pt-1">
              + {items.length - recent.length} {items.length - recent.length === 1 ? 'till produkt' : 'till produkter'} i varukorgen
            </p>
          )}
        </div>

        {/* Footer: total + CTAs */}
        <div className="border-t bg-gray-50 px-4 sm:px-5 py-3 sm:py-4 space-y-3">
          {/* Free shipping nudge */}
          {remainingToFreeShipping > 0 ? (
            <div className="text-xs text-gray-700">
              <div className="flex justify-between mb-1">
                <span>{formatPrice(remainingToFreeShipping)} kvar till fri frakt</span>
                <span className="font-semibold">{formatPrice(total)} / {formatPrice(FREE_SHIPPING_THRESHOLD)}</span>
              </div>
              <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
                <div
                  className="h-full bg-amber-400 transition-all duration-500"
                  style={{ width: `${Math.min(100, (total / FREE_SHIPPING_THRESHOLD) * 100)}%` }}
                />
              </div>
            </div>
          ) : fraktoffert && fraktoffert.surcharge > 0 ? (
            <p className="text-xs font-medium text-amber-700">
              Frakt {formatPrice(fraktoffert.shipping)} — {fraktoffert.surcharge_label?.toLowerCase()} i beställningen
              ({formatPrice(fraktoffert.surcharge)} tillägg). Fri frakt gäller inte skrymmande gods.
            </p>
          ) : (
            <p className="text-xs font-medium text-green-700 flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5" />
              Fri frakt — gratis hemleverans 2–5 dagar
            </p>
          )}

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Totalt</span>
            <span className="text-2xl font-bold text-gray-900">{formatPrice(total)}</span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={close}
              className="px-3 py-2.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700"
            >
              Fortsätt handla
            </button>
            <Link
              href="/kassa"
              onClick={close}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-[#0055A4] hover:bg-[#0f2e5a] text-sm font-semibold text-white shadow-sm"
            >
              Till kassan
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <Link
            href="/varukorg"
            onClick={close}
            className="block text-center text-xs text-gray-500 hover:text-gray-800 underline"
          >
            <ShoppingCart className="h-3.5 w-3.5 inline mr-1" />
            Se varukorgen ({count})
          </Link>
        </div>
      </div>
    </>
  );
}
